/**
 * THE CAR, FROM ABOVE.
 *
 * The printed PLAAY driver card carries an OVERHEAD drawing of a stock car —
 * nose to the left, four tyres proud of the bodywork, the number filling the
 * roof, a little vertical manufacturer tab on the nose. It is about twice as
 * wide as it is tall. This is that drawing, in SVG, with a livery on it — and
 * with `printed` it is that drawing as the card actually prints it.
 *
 * WHY THE PLAN VIEW IS BUILT THE WAY IT IS. A car that reads at 200px and turns
 * into a coloured lozenge at 60px is worthless here, because 60px is where it
 * actually lives: forty-three of these sit on the boards at once and most of
 * them are on a 118px card. From above, three things carry the read, and every
 * decision below serves one of them:
 *
 *   1. THE TYRES. Four black blocks sticking out past the body at the corners.
 *      They are drawn FIRST, under the body, so only the outer nub shows — that
 *      is what a wheel behind a fender looks like from a helicopter, and at
 *      card size the four nubs are the single strongest "this is a car" signal.
 *      They carry a pale hairline so a black car does not swallow them.
 *   2. THE GREENHOUSE. A dark windscreen band and a dark backlight band with a
 *      light roof panel between them. Two dark bars across a coloured body is
 *      unmistakable even when it is nine pixels tall.
 *   3. THE TAPER. The nose is narrower than the tail and the tab sits on it, so
 *      the car has a direction rather than being a symmetrical pill.
 *
 * Everything else — hood and deck panel seams, the spoiler bar, the decal — is
 * detail that pays off at 200px and is allowed to disappear at 60px. Nothing
 * carries information.
 *
 * THE FIVE SCHEMES are drawn as crude shapes CLIPPED TO THE BODY, which is what
 * lets them stay crude and still come out looking sprayed on. `roof-panel` is
 * the only one that touches the roof, and `livery.ts` knows it: `numberPanel`
 * is the colour the number was contrast-checked against, so the number is legal
 * on every scheme without this file having to think about it.
 *
 * DECORATIVE. `aria-hidden` on the root: the number and the driver's name are
 * published as text by the board's running order. Nothing here carries
 * information that is not also somewhere a screen reader can reach.
 */

import { useId } from 'react';
import type { Livery } from '../domain/livery.ts';

/** The drawing's own units. Roughly 2:1 — the proportion of the printed art. */
export const VIEW_W = 200;
export const VIEW_H = 100;

/** The car's centreline. Everything below is symmetric about it. */
const CY = 50;

/**
 * The bodyshell, seen from above: blunt nose at the left, flaring to the front
 * fenders, straight down the flanks, and a flat tail panel at the right.
 * Symmetric about y=50 by construction — the bottom half is the top half's
 * control points in reverse.
 */
const BODY =
  'M12 35 ' +
  'C12 29 15 25.5 22 24 ' + // nose radius
  'C32 22 42 20.8 54 19.6 ' + // front fender flare
  'C66 18.5 76 17.7 92 17.3 ' +
  'L150 17 ' + // flank, widest through the rear wheels
  'C170 17 182 17.8 189 19.8 ' + // rear quarter
  'C192.5 20.8 194 22.6 194 25.5 ' +
  'L194 74.5 ' + // tail panel
  'C194 77.4 192.5 79.2 189 80.2 ' +
  'C182 82.2 170 83 150 83 ' +
  'L92 82.7 ' +
  'C76 82.3 66 81.5 54 80.4 ' +
  'C42 79.2 32 78 22 76 ' +
  'C15 74.5 12 71 12 65 ' +
  'Z';

/**
 * The greenhouse: windscreen, roof, backlight. Narrower than the body — the
 * gap left on each side is the roof rail, and it is what stops the glass from
 * reading as a stripe painted across the whole car.
 *
 * The glass does NOT touch the roof. The 1.5-unit strip of body colour between
 * each band and the roof panel is the A- and C-pillar, and it is the single
 * cheapest detail in the drawing: without it the three panels merge into one
 * banded slab and the car turns into a bus.
 */
const WINDSCREEN = 'M76 33 L94 29 L94 71 L76 67 Z';
const ROOF = 'M95.5 29 L138 29 L138 71 L95.5 71 Z';
const BACKLIGHT = 'M139.5 29 L156 31.5 L156 68.5 L139.5 71 Z';
const GREENHOUSE = 'M76 33 L95 29 L138 29 L156 31.5 L156 68.5 L138 71 L95 71 L76 67 Z';

/**
 * Panel gaps, and they run LENGTHWAYS. The seam a plan view actually shows is
 * the hood-to-fender gap and the deck-to-quarter gap — two long lines a couple
 * of units in from each flank. Drawn as boxed panels instead, they read as a
 * second windscreen and wreck the car.
 */
const SEAMS = [
  'M22 26.6 L74 23.4', // hood / left fender
  'M22 73.4 L74 76.6', // hood / right fender
  'M74 23.4 L74 76.6', // the cowl: the hood's trailing edge, at the screen
  'M160 22.6 L187 25', // deck / left quarter
  'M160 77.4 L187 75', // deck / right quarter
];

/** The rear wing, seen edge-on from above as a bar across the tail. */
const SPOILER = { x: 186, y: 23, w: 6.5, h: 54 };

/**
 * Four tyres, drawn BEFORE the body so only the part outside the fender shows.
 * Front pair sits under the fender flare, rear pair under the quarter panel.
 */
const TYRE_W = 28;
const TYRE_H = 21;
const TYRES = [
  { x: 36, y: 8 },
  { x: 134, y: 8 },
  { x: 36, y: 71 },
  { x: 134, y: 71 },
];

/** The manufacturer tab: a vertical block on the nose with the make set sideways. */
const TAB = { x: 16, y: 35, w: 11, h: 30 };

/** The number, filling the roof panel. 40 of 100 units — the roof is 50 tall. */
const NUMBER = { x: 116.75, y: CY, size: 36 };

/** The decal, centred on the rear deck between the backlight and the wing. */
const DECAL = { cx: 172, cy: CY };

/**
 * THE HOOD, as a box a team's mark is dropped into.
 *
 * Howard, 17 Aug 2026: *"can we have those logos show on the hood's of the
 * appropriate cars on the race grid and screens?"*
 *
 * The hood runs from the nose seam at x=22 to the cowl at x=74, and the fender
 * seams pinch it from y≈26.6 to y≈73.4 at the nose. This box is inset a couple
 * of units inside all four of those so a square mark never crosses a panel gap
 * — a logo lying across a seam reads as a sticker peeling off, not as paint.
 *
 * It is DELIBERATELY not the full hood. `preserveAspectRatio="xMidYMid meet"`
 * fits the mark inside the box whatever shape it is, so a wide wordmark and a
 * square roundel both land centred and neither is stretched. A logo distorted
 * to fill a panel is worse than a small one.
 */
const HOOD = { x: 27, y: 30, w: 42, h: 40 };

/**
 * Mix two `#rrggbb` colours, `t` of the way from `a` to `b`.
 *
 * Straight sRGB, deliberately: this is picking a plausible ink, not doing
 * colour science, and a gamma-correct mix of a saturated body colour with a
 * neutral comes out lighter than a paint shop would spray it.
 */
function mix(a: string, b: string, t: number): string {
  const at = (h: string, i: number): number => parseInt(h.slice(i, i + 2), 16);
  const ch = (i: number): string =>
    Math.round(at(a, i) * (1 - t) + at(b, i) * t)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(1)}${ch(3)}${ch(5)}`;
}

/**
 * The glass, and it is the one place this drawing knowingly departs from the
 * printed card.
 *
 * The card tints its glass DARKER than the body, which works in print because
 * every printed car is black on cream. Painted, it does not: a dark tint on a
 * navy or a black car is a hole punched through the roof, and at 60px the whole
 * greenhouse turns into one blob. So the glass is pulled hard toward a fixed
 * mid-slate and keeps only a whisper of the body's hue — darker than a white
 * car, lighter than a black one, always visibly NOT the bodywork.
 */
const GLASS_GREY = '#5C636E';
const GLASS_PULL = 0.82;

/** A five-pointed star, one point up, as a polygon `points` string. */
function starPoints(cx: number, cy: number, outer: number, inner: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
}

export interface CarTopDownProps {
  livery: Livery;
  /** Printed as it is on the card: `0`, `02` and a bare `X` are all real. */
  carNumber: string;
  /** Rendered width in px. Height is half of it. Default 104. */
  size?: number;
  /** Faithful mode: the printed black car, no colour, no gloss. */
  printed?: boolean;
  /**
   * The shop's mark, painted on the hood. Null for a shop that has none, which
   * is every shop until somebody uploads one — the car simply has a plain hood.
   *
   * HIDDEN IN `printed` MODE, with the decals and the colour. "As printed"
   * means the black car exactly as it is on the physical card, and a logo is an
   * addition of ours; leaving it on would make the faithful view unfaithful in
   * the one way it exists to avoid.
   */
  hoodLogoUrl?: string | null;
  /**
   * What the mark is of, for assistive technology. Ignored when there is no
   * mark. A hood logo is decoration layered on a car that is ALREADY named in
   * the running order beside it, so this is a title on the group rather than a
   * second announcement of the same car.
   */
  hoodLogoLabel?: string | null;
  className?: string;
}

export default function CarTopDown({
  livery,
  carNumber,
  size = 104,
  printed = false,
  hoodLogoUrl = null,
  hoodLogoLabel = null,
  className,
}: CarTopDownProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const clipId = `carclip-${uid}`;
  const shadeId = `carshade-${uid}`;

  const { base, accent, numberColor, numberOutline, scheme, decal } = livery;

  const edge = printed ? 'rgba(0,0,0,0.92)' : 'rgba(8,8,10,0.82)';
  // Panel seams have to survive on a near-black body as well as a white one, so
  // in printed mode (the body IS near-black) they invert.
  const seam = printed ? 'rgba(245,241,230,0.34)' : 'rgba(0,0,0,0.34)';
  const glass = printed ? '#4A4738' : mix(livery.base, GLASS_GREY, GLASS_PULL);
  const rubber = printed ? '#16150F' : '#0E0E11';

  // The roof is the accent only on `roof-panel`; the printed card has no accent.
  const roofFill = !printed && scheme === 'roof-panel' ? accent : base;
  // The deck is the accent on `two-tone-tail`, and an accent decal on an accent
  // deck is no decal at all — swap to the base, which is guaranteed to contrast.
  const deckIsAccent = !printed && scheme === 'two-tone-tail';
  const decalFill = deckIsAccent ? base : accent;

  return (
    <svg
      className={className}
      width={size}
      height={(size * VIEW_H) / VIEW_W}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <path d={BODY} />
        </clipPath>
        {!printed && (
          // One light source, high and to the near side: a highlight along the
          // upper flank and a shadow along the lower one. Deliberately weak —
          // strong gloss on a plan view reads as a dent.
          <linearGradient id={shadeId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.13" />
            <stop offset="40%" stopColor="#ffffff" stopOpacity="0.02" />
            <stop offset="64%" stopColor="#000000" stopOpacity="0.02" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.17" />
          </linearGradient>
        )}
      </defs>

      {/*
        Tyres first, under the bodywork. The pale hairline is the whole reason a
        black car still has visible wheels.
      */}
      {TYRES.map((t) => (
        <rect
          key={`t-${t.x}-${t.y}`}
          x={t.x}
          y={t.y}
          width={TYRE_W}
          height={TYRE_H}
          rx="1.8"
          fill={rubber}
          stroke="rgba(146,152,163,0.5)"
          strokeWidth="0.9"
        />
      ))}

      {/* Body in the base colour. */}
      <path d={BODY} fill={base} />

      {/* The scheme, sprayed on and masked to the bodywork. */}
      <g clipPath={`url(#${clipId})`}>
        {!printed && scheme === 'two-tone-nose' && (
          // Front third, cut with a shallow chevron pointing back — the split a
          // painted nose actually has.
          <polygon points="0,0 72,0 82,50 72,100 0,100" fill={accent} />
        )}
        {!printed && scheme === 'two-tone-tail' && (
          // Rear third, chevron pointing forward.
          <polygon points="200,0 156,0 146,50 156,100 200,100" fill={accent} />
        )}
        {!printed && scheme === 'centre-stripe' && (
          // Nose to tail down the centreline. The greenhouse is drawn over it,
          // so on the finished car it runs up the hood, under the glass and out
          // across the deck — which is where a roof number leaves room for it.
          <rect x="0" y="38" width={VIEW_W} height="24" fill={accent} />
        )}
        {!printed && <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill={`url(#${shadeId})`} />}
      </g>

      {/* Panel gaps down the hood and the deck, and the wing across the tail. */}
      {SEAMS.map((d) => (
        <path key={d} d={d} fill="none" stroke={seam} strokeWidth="1" strokeLinecap="round" />
      ))}
      <rect
        x={SPOILER.x}
        y={SPOILER.y}
        width={SPOILER.w}
        height={SPOILER.h}
        rx="1.4"
        fill={printed ? 'rgba(245,241,230,0.16)' : 'rgba(0,0,0,0.3)'}
        stroke={seam}
        strokeWidth="1"
      />

      {/*
        THE TEAM'S MARK ON THE HOOD.

        An <image>, never an inline <svg>. Marks may be uploaded as SVG — a
        crest usually is vector — and an SVG is a document that can carry
        script. Inlined into this DOM it would run here; through an <img> it
        cannot, in any browser. That is why this is an <image> and not a
        cleverer thing that could be styled. See `worker/logo.ts`.

        Drawn AFTER the body and its seams, so it sits on the paint, and BEFORE
        the greenhouse and the number, so nothing about the car's identity is
        ever obscured by decoration.
      */}
      {!printed && hoodLogoUrl && (
        <image
          href={hoodLogoUrl}
          x={HOOD.x}
          y={HOOD.y}
          width={HOOD.w}
          height={HOOD.h}
          preserveAspectRatio="xMidYMid meet"
          // A mark that fails to load leaves a plain hood rather than a broken
          // image icon in the middle of a race board.
          onError={(e) => {
            (e.currentTarget as SVGImageElement).style.display = 'none';
          }}
        >
          {hoodLogoLabel ? <title>{hoodLogoLabel}</title> : null}
        </image>
      )}

      {/* The decal on the rear deck. */}
      {!printed && decal === 'oval' && (
        <ellipse
          cx={DECAL.cx}
          cy={DECAL.cy}
          rx="12"
          ry="8"
          fill={decalFill}
          stroke="rgba(0,0,0,0.45)"
          strokeWidth="0.9"
        />
      )}
      {!printed && decal === 'roundel' && (
        <g>
          <circle
            cx={DECAL.cx}
            cy={DECAL.cy}
            r="10"
            fill={decalFill}
            stroke="rgba(0,0,0,0.45)"
            strokeWidth="0.9"
          />
          <circle
            cx={DECAL.cx}
            cy={DECAL.cy}
            r="5.4"
            fill="none"
            stroke={deckIsAccent ? accent : base}
            strokeWidth="2.2"
          />
        </g>
      )}
      {!printed && decal === 'star' && (
        <polygon
          points={starPoints(DECAL.cx, DECAL.cy, 11.5, 4.9)}
          fill={decalFill}
          stroke="rgba(0,0,0,0.45)"
          strokeWidth="0.9"
          strokeLinejoin="round"
        />
      )}

      {/*
        The roof, then the two bands of glass. On `roof-panel` the accent is
        laid over the WHOLE greenhouse — pillars and roof rails included — so it
        reads as a roof that has been painted, the way #6's silver-grey roof
        does, rather than a plate screwed to the top of the car. Elsewhere the
        roof carries a hairline of its own so that on a `solid` car, where roof
        and body are one colour, the number still sits on something that reads
        as a panel.
      */}
      {!printed && scheme === 'roof-panel' && <path d={GREENHOUSE} fill={accent} />}
      <path d={ROOF} fill={roofFill} />
      {/*
        NOTHING is laid over the roof — no gloss, no tint, no shading. The roof
        is `numberPanel` exactly, which is the colour `livery.ts` ran the
        contrast test against; a 6% white gloss here is enough to drop a
        marginal pair under 4.5:1, so the panel is left alone and the roof is
        separated by its hairline instead.
      */}
      <path d={ROOF} fill="none" stroke={edge} strokeWidth="1.1" strokeLinejoin="round" />
      <path d={WINDSCREEN} fill={glass} />
      <path d={BACKLIGHT} fill={glass} />
      {!printed && <path d="M76 33 L94 29 L94 35.5 L76 39 Z" fill="#ffffff" fillOpacity="0.16" />}
      <path d={GREENHOUSE} fill="none" stroke={edge} strokeWidth="1.3" strokeLinejoin="round" />

      {/* Bodywork outline, over the paint, so every panel keeps its edge. */}
      <path d={BODY} fill="none" stroke={edge} strokeWidth="1.8" strokeLinejoin="round" />

      {/* The manufacturer tab, on the nose, set sideways the way the card sets it. */}
      <rect
        x={TAB.x}
        y={TAB.y}
        width={TAB.w}
        height={TAB.h}
        rx="1.6"
        fill={printed ? '#F5F1E6' : livery.tabColor}
        stroke={edge}
        strokeWidth="0.9"
      />
      <text
        x={TAB.x + TAB.w / 2}
        y={TAB.y + TAB.h / 2}
        transform={`rotate(-90 ${TAB.x + TAB.w / 2} ${TAB.y + TAB.h / 2})`}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily='"Arial Narrow", "Roboto Condensed", "Helvetica Neue", ui-sans-serif, sans-serif'
        fontSize="7.2"
        fontWeight={700}
        letterSpacing="0.3"
        fill={printed ? '#16150F' : livery.tabInk}
      >
        {livery.manufacturer}
      </text>

      {/* The number, on the roof, where the card puts it. */}
      <text
        x={NUMBER.x}
        y={NUMBER.y}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily='"Arial Narrow", "Roboto Condensed", "Helvetica Neue", ui-sans-serif, sans-serif'
        fontSize={NUMBER.size}
        fontWeight={800}
        letterSpacing="-1"
        fill={numberColor}
        stroke={numberOutline ?? 'none'}
        strokeWidth={numberOutline ? 2.4 : 0}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {carNumber}
      </text>
    </svg>
  );
}
